// Initialises Sentry error tracking on the frontend. Called once from
// main.tsx before React mounts, so uncaught errors and route-level crashes
// are captured even if they happen during the initial render.
//
// Safe to call with an empty DSN — Sentry's init becomes a no-op, so dev
// builds without VITE_SENTRY_DSN configured don't emit any events. Matches
// the backend's empty-DSN handling.
import * as Sentry from '@sentry/react'

interface ApiErrorLike {
  status?: number
}

function isExpectedApiError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false
  const e = err as ApiErrorLike
  if (typeof e.status !== 'number') return false
  // 4xx responses are user-facing validation / auth errors, not bugs.
  // 5xx stays captured. Network errors (fetch throws) also stay.
  return e.status >= 400 && e.status < 500
}

// redactToken replaces the value of any `token` query parameter with
// `[redacted]`. The reset-password and email-change-confirm flows put a
// raw single-use token in the URL; without scrubbing, Sentry's automatic
// navigation breadcrumbs and `event.request.url` would carry it. We can't
// use the URL constructor on every input (breadcrumb URLs are sometimes
// path-only or templated), so a regex replace keeps it predictable.
export function redactToken(input: string): string {
  if (!input) return input
  return input.replace(/(\btoken=)[^&#\s"']+/gi, '$1[redacted]')
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) return

  Sentry.init({
    dsn,
    // MODE is 'production' for vite build, 'development' for vite dev.
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
    // No performance tracing or session replay pre-launch. See
    // DECISIONS.md 2026-04-23 "Sentry integration".
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Strip `?token=...` from navigation breadcrumb URLs and from
    // console/error breadcrumb messages that quote a URL inline. We
    // mutate `data.from` / `data.to` / `data.url` / `message` rather
    // than dropping the breadcrumb so the navigation trail stays
    // intact for debugging — only the secret value is removed.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        for (const key of ['url', 'from', 'to'] as const) {
          const v = breadcrumb.data[key]
          if (typeof v === 'string') {
            breadcrumb.data[key] = redactToken(v)
          }
        }
      }
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = redactToken(breadcrumb.message)
      }
      return breadcrumb
    },
    beforeSend(event, hint) {
      if (isExpectedApiError(hint?.originalException)) {
        return null
      }
      if (event.request?.url) {
        event.request.url = redactToken(event.request.url)
      }
      if (event.request?.query_string && typeof event.request.query_string === 'string') {
        event.request.query_string = redactToken(event.request.query_string)
      }
      return event
    },
  })
}
